'use client';

import Link from 'next/link';
import type { ReactNode } from 'react';
import { Thumbnail } from '@/app/saved/components/Thumbnail';
import { designPath, type PublicDesign } from '@/app/lib/community/shared';
import { CLIENT_ADAPTERS } from '@/app/lib/community/types';
import SectionText from '@/app/components/minecraft/SectionText';

/** A type's own gallery preview when its adapter has one (skins), else the saved library's `Thumbnail`. */
export function DesignThumb({ design, size }: { design: PublicDesign; size: 'grid' | 'list' | 'modal' }) {
  const adapter = CLIENT_ADAPTERS[design.type];
  const Preview = adapter.Preview;
  return Preview ? <Preview design={design} size={size} /> : <Thumbnail item={adapter.toLibraryItem(design)} size={size} />;
}

/**
 * One design in a gallery grid. The whole card is the link, so it is one tab stop.
 *
 * `footer` (My Library → Published: status + Manage) renders inside the same card frame but OUTSIDE the
 * link — a link can't contain another link. Rendered as a sibling below the card, that row floated
 * disconnected from the design it belonged to.
 */
export default function DesignCard({ design, footer }: { design: PublicDesign; footer?: ReactNode }) {
  if (footer) {
    return (
      <div className="bg-zinc-900 rounded-xl border border-zinc-800 hover:border-zinc-600 focus-within:border-emerald-500 overflow-hidden transition-colors">
        <CardLink design={design} framed={false} />
        <div className="border-t border-zinc-800 px-3 py-2">{footer}</div>
      </div>
    );
  }
  return <CardLink design={design} framed />;
}

function CardLink({ design, framed }: { design: PublicDesign; framed: boolean }) {
  return (
    <Link
      href={designPath(design.type, design.id)}
      className={`group block ${framed ? 'bg-zinc-900 rounded-xl border border-zinc-800 hover:border-zinc-600 focus-visible:border-emerald-500 overflow-hidden press transition-[border-color,scale] duration-150 ease-out active:scale-[0.98] motion-reduce:active:scale-100' : 'focus-visible:outline-none'}`}
    >
      <div className="aspect-[4/5] bg-zinc-950">
        <DesignThumb design={design} size="grid" />
      </div>
      <div className="px-3 py-2.5">
        <div dir="auto" className="text-sm font-medium text-zinc-100 truncate group-hover:text-white"><SectionText text={design.title} /></div>
        {/* Author and counts on separate rows: sharing one, a long username was squeezed to "by SavedT…". */}
        <div className="mt-0.5 text-xs text-zinc-500">
          <div className="truncate">by {design.authorUsername}</div>
          <div className="mt-0.5 flex flex-wrap items-center gap-x-2 tabular-nums">
            <span className="flex items-center gap-0.5" title={`${design.likeCount.toLocaleString('en-US')} ${design.likeCount === 1 ? 'like' : 'likes'}`}>
              <svg aria-hidden="true" className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
              </svg>
              {design.likeCount.toLocaleString('en-US')}
              <span className="sr-only"> {design.likeCount === 1 ? 'like' : 'likes'},</span>
            </span>
            <span className="flex items-center gap-0.5" title={`${design.copyCount.toLocaleString('en-US')} ${design.copyCount === 1 ? 'copy' : 'copies'}`}>
              <svg aria-hidden="true" className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              {design.copyCount.toLocaleString('en-US')}
              <span className="sr-only"> {design.copyCount === 1 ? 'copy' : 'copies'}</span>
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
}
